import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';

/**
 * Where a connection attempt began. Rides on every `profile.connect.*` event, so it is a closed set
 * on purpose: a typo in an analytics value is silent at runtime and permanent in the data.
 */
export type ConnectSurface = 'onboarding' | 'onboarding-dev' | 'connect-dialog' | 'brands';

/**
 * Opens new window with the legacy oauth process for the network provided.
 *
 * The server only emits `profile.add` once a connection succeeds, so the client is the only place
 * that can see an attempt which never got that far. The events below bracket the popup:
 * `profile.connect.started` when it opens and `profile.connect.window-closed` when the user closes
 * it. A started attempt with no `profile.add` after it is an abandoned connection.
 *
 * `onClose` runs whenever the popup closes, which says nothing about the outcome: the legacy pages
 * close it the same way on success, error and cancel. NetCard asks the server afterwards and
 * captures `profile.connect.failed` when nothing was saved.
 *
 * @param surface where the attempt began, e.g. 'onboarding' or 'connect-dialog'. Optional, so the
 * re-authorisation call sites keep working unchanged.
 * @returns Boolean whether the window was opened (true = opened)
 */
export const updateProfile = (
    netCode: string,
    onClose: () => void,
    update: boolean = true,
    brandId?: number,
    surface?: ConnectSurface,
) => {
    const baseParam = update ? '?refresh' : '?new_account';
    const brandParam = brandId != null ? `&brand=${brandId}` : '';
    const net = netCode.toLowerCase();
    const openedAt = Date.now();

    if (POSTHOG_ENABLED) {
        posthog.capture('profile.connect.started', { netCode: net, update, surface });
    }

    const win = window.open(
        `${import.meta.env.VITE_OLD_LEGACY_APP_ROOT}/OAuth/${net}.php` + baseParam + brandParam,
        '_blank',
        '',
    );
    if (win) {
        win.focus();
        let retries = 0;
        const timer = setInterval(function () {
            if (win.closed) {
                if (POSTHOG_ENABLED) {
                    posthog.capture('profile.connect.window-closed', {
                        netCode: net,
                        update,
                        surface,
                        secondsOpen: Math.round((Date.now() - openedAt) / 1000),
                    });
                }
                setTimeout(() => {
                    onClose();
                }, 1500);
            }
            if (win.closed || ++retries == 5 * 60) {
                clearInterval(timer);
            }
        }, 1000);
    } else {
        // Popup blocked. Worth its own event: from the server it is indistinguishable from a user
        // who never clicked, and the caller needs to know so it can stop showing a spinner.
        if (POSTHOG_ENABLED) {
            posthog.capture('profile.connect.blocked', { netCode: net, update, surface });
        }
        return false;
    }
    return true;
};
